const DEFAULT_STATE = Object.freeze({ base: 'all', diet: 'all', viral: false, q: '' });

class RecipeFilter extends HTMLElement {
  connectedCallback() {
    this.state = { ...DEFAULT_STATE };
    this.cards = Array.from(this.querySelectorAll('[data-recipe-card]'));
    this.menus = Array.from(this.querySelectorAll('[data-menu]'));
    this.options = Array.from(this.querySelectorAll('[data-option]'));
    this.searchInput = this.querySelector('[data-search-input]');
    this.viralToggle = this.querySelector('[data-viral-toggle]');
    this.countEl = this.querySelector('[data-count]');
    this.activeEl = this.querySelector('[data-active-filters]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.labels = {};
    this.options.forEach((option) => {
      this.labels[`${option.dataset.filter}:${option.dataset.value}`] = option.textContent.trim();
      option.addEventListener('click', () => {
        this.state[option.dataset.filter] = option.dataset.value;
        this.closeMenus();
        this.update();
      });
    });

    this.viralLabel = this.viralToggle ? this.viralToggle.textContent.trim() : 'Viral';

    this.menus.forEach((menu) => this.bindMenu(menu));

    this.viralToggle?.addEventListener('click', () => {
      this.state.viral = !this.state.viral;
      this.update();
    });

    this.searchInput?.addEventListener('input', () => {
      this.state.q = this.searchInput.value;
      this.update({ fromSearch: true });
    });

    this.querySelectorAll('[data-reset]').forEach((btn) => {
      btn.addEventListener('click', () => this.reset());
    });

    this.onDocumentClick = (event) => {
      if (!event.target.closest('[data-menu]')) this.closeMenus();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const open = this.menus.find((menu) => menu.classList.contains('is-open'));
      if (open) {
        this.closeMenus();
        open.querySelector('[data-menu-trigger]')?.focus();
      }
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.readUrl();
    this.update({ skipUrl: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  bindMenu(menu) {
    const trigger = menu.querySelector('[data-menu-trigger]');
    if (!trigger) return;

    trigger.addEventListener('click', () => {
      const isOpen = menu.classList.contains('is-open');
      if (isOpen && menu.dataset.openedByHover === 'true') {
        // Pointer opened it on hover; a click should keep it open, not close it.
        menu.dataset.openedByHover = 'false';
        return;
      }
      this.closeMenus();
      if (!isOpen) this.openMenu(menu, false);
    });

    menu.addEventListener('mouseenter', () => {
      if (!this.hoverQuery.matches) return;
      this.closeMenus();
      this.openMenu(menu, true);
    });

    menu.addEventListener('mouseleave', () => {
      if (!this.hoverQuery.matches) return;
      this.closeMenu(menu);
    });
  }

  openMenu(menu, byHover) {
    menu.classList.add('is-open');
    menu.dataset.openedByHover = byHover ? 'true' : 'false';
    menu.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  closeMenu(menu) {
    menu.classList.remove('is-open');
    menu.dataset.openedByHover = 'false';
    menu.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  closeMenus() {
    this.menus.forEach((menu) => this.closeMenu(menu));
  }

  reset() {
    this.state = { ...DEFAULT_STATE };
    if (this.searchInput) this.searchInput.value = '';
    this.closeMenus();
    this.update();
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    const base = params.get('base');
    const diet = params.get('diet');
    if (base && this.labels[`base:${base}`]) this.state.base = base;
    if (diet && this.labels[`diet:${diet}`]) this.state.diet = diet;
    if (params.get('viral') === '1') this.state.viral = true;
    const q = params.get('q');
    if (q) {
      this.state.q = q;
      if (this.searchInput) this.searchInput.value = q;
    }
  }

  writeUrl() {
    const url = new URL(window.location.href);
    const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
    set('base', this.state.base !== 'all' ? this.state.base : '');
    set('diet', this.state.diet !== 'all' ? this.state.diet : '');
    set('viral', this.state.viral ? '1' : '');
    set('q', this.state.q.trim());
    window.history.replaceState(window.history.state, '', url);
  }

  update({ skipUrl = false } = {}) {
    const { base, diet, viral } = this.state;
    const terms = this.state.q.toLowerCase().trim().split(/\s+/).filter(Boolean);

    this.options.forEach((option) => {
      const active = this.state[option.dataset.filter] === option.dataset.value;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-pressed', String(active));
    });

    this.menus.forEach((menu) => {
      const filter = menu.querySelector('[data-option]')?.dataset.filter;
      menu.querySelector('[data-menu-trigger]')?.classList.toggle('has-value', !!filter && this.state[filter] !== 'all');
    });

    this.viralToggle?.setAttribute('aria-pressed', String(viral));

    let visible = 0;
    this.cards.forEach((card) => {
      const diets = (card.dataset.diet || '').split(' ').filter(Boolean);
      const haystack = card.dataset.search || '';
      const match =
        (base === 'all' || card.dataset.base === base) &&
        (diet === 'all' || diets.includes(diet)) &&
        (!viral || card.dataset.viral === 'true') &&
        terms.every((term) => haystack.includes(term));

      card.hidden = !match;
      if (match) visible += 1;
    });

    if (this.countEl) {
      this.countEl.textContent = `${visible} ${visible === 1 ? 'recipe' : 'recipes'}`;
    }
    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;

    this.renderActive();
    if (!skipUrl) this.writeUrl();
  }

  renderActive() {
    if (!this.activeEl) return;
    this.activeEl.replaceChildren();

    const chips = [];
    if (this.state.base !== 'all') {
      chips.push([this.labels[`base:${this.state.base}`], () => (this.state.base = 'all')]);
    }
    if (this.state.diet !== 'all') {
      chips.push([this.labels[`diet:${this.state.diet}`], () => (this.state.diet = 'all')]);
    }
    if (this.state.viral) {
      chips.push([this.viralLabel, () => (this.state.viral = false)]);
    }
    if (this.state.q.trim()) {
      chips.push([
        `“${this.state.q.trim()}”`,
        () => {
          this.state.q = '';
          if (this.searchInput) this.searchInput.value = '';
        },
      ]);
    }

    chips.forEach(([label, clear]) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'rf-chip';
      chip.setAttribute('aria-label', `Remove filter: ${label}`);
      const text = document.createElement('span');
      text.textContent = label;
      const x = document.createElement('span');
      x.className = 'rf-chip__x';
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';
      chip.append(text, x);
      chip.addEventListener('click', () => {
        clear();
        this.update();
      });
      this.activeEl.append(chip);
    });

    if (chips.length > 1) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.className = 'rf-clear';
      clearAll.textContent = 'Clear all';
      clearAll.addEventListener('click', () => this.reset());
      this.activeEl.append(clearAll);
    }
  }
}

if (!customElements.get('recipe-filter')) {
  customElements.define('recipe-filter', RecipeFilter);
}
